import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { BEIGE_400, GRAY_700 } from 'constants/styling/theme'
import { EntityKeys, QueryType } from 'utils/reactQuery'
import { MUIButton } from 'components/common/MUIButton'
import { SystemMessage } from 'components/common/SystemMessage'
import { usePatchPropertyDetail } from 'dataQueries/property.query'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import type { PropertyAddressDTO } from 'models/property'
import { ProductSegment } from 'constants/product/productTypes'
import { AddressData, AddressSection } from './AddressSection.component'
import { AreaRangeFields } from './AreaRangeFields.component'
import { usePfEffectiveWorkspaceId } from './usePfEffectiveWorkspaceId'
import { ADDRESS_REQUIRED_FIELDS, missingAddressTextFields, type RequiredPropertyField } from './usePropertyCompleteness'

interface Props {
  propertyId: string
  missing: ReadonlyArray<RequiredPropertyField>
  onCompleted: () => void
  /** The caller saves through the ref instead, from its own button. */
  hideSubmit?: boolean
  onStateChange?: (state: { canSave: boolean; isSaving: boolean }) => void
}

/** What a saved completion yields: the re-entered address and its coordinates, and the picked area range, when missing. */
export interface PropertyCompletionResult {
  coordinates?: { lat: number; lng: number }
  address?: PropertyAddressDTO
  areaRange?: ProductSegment
}

export interface PropertyCompletionHandle {
  /** Saves the missing fields; resolves null when nothing was saved. */
  submit: () => Promise<PropertyCompletionResult | null>
}

/** Has the re-entered address produced routable (non-null-island) coordinates? */
const hasRoutableCoordinates = (coords?: { lat: number; lng: number }): boolean =>
  !!coords && coords.lat !== 0 && coords.lng !== 0

/** Build a `PropertyAddressDTO` from the AddressSection result. Returns null when
 *  the address is missing a required text field or routable coordinates — either
 *  would 422 server-side, and PATCHing it would leave the CTA locked with no
 *  explanation of what is still wrong.
 *
 *  The text-field check is defensive: `AddressSection` no longer emits an
 *  incomplete address on either sub-mode. It sits here because this is the write
 *  boundary — the rule belongs next to the PATCH, not only next to the input. */
const toPropertyAddress = (data: AddressData | null): PropertyAddressDTO | null => {
  if (!data) return null

  if (!data.isManual) {
    const addr = data.propertyAddress
    if (!addr || !hasRoutableCoordinates(addr.coordinates)) return null
    if (missingAddressTextFields(addr).length > 0) return null
    return addr
  }

  // Manual entry — only valid once the silent geocode produced coordinates.
  if (!hasRoutableCoordinates(data.coordinates)) return null
  if (missingAddressTextFields(data).length > 0) return null
  return {
    street: data.street,
    streetNumber: data.streetNumber,
    city: data.city,
    postalCode: data.postalCode,
    country: data.country,
    regionCode: '',
    coordinates: data.coordinates,
  }
}

/**
 * Inline completion form rendered inside PropertySelectionDrawer (Phase 5b;
 * Slice C fix-wave).
 *
 * When a selected property is missing PF-mandatory fields, this form collects
 * only the missing values and PATCHes them. On success, React Query caches are
 * invalidated so usePropertyCompleteness re-evaluates and the "Next step" CTA
 * unlocks automatically.
 *
 * Scope:
 *   - address (street / streetNumber / postalCode / city) + routable coordinates
 *     — re-entered via the SHARED `AddressSection` (Google Places autofill or
 *     manual + geocode), the same component the create-property form uses. This
 *     is reuse, not a new editor: re-selecting an address yields routable
 *     coordinates, which is the only way to clear the `(0,0)` BE check.
 *   - the property's size, as an area range (ISSUE-4477): a property with neither an
 *     area nor a range fails session-create, so the range is collected here and sent
 *     as `areaRange` on the patch. The purchase flow asks for no exact area.
 */
export const PropertyCompletionForm = forwardRef<PropertyCompletionHandle, Props>(({ propertyId, missing, onCompleted, hideSubmit = false, onStateChange }, ref) => {
  const { t } = useTranslation(['purchase_flow_revamp'])
  // Admin acting for a client patches the CLIENT's workspace-scoped property.
  const workspaceId = usePfEffectiveWorkspaceId()
  const { spawnErrorToast, spawnSuccessToast } = useSnackbar()
  const queryClient = useQueryClient()
  // `asClient`: the property endpoints serve the client representation only —
  // an admin patching a client's property would otherwise 406.
  const patchProperty = usePatchPropertyDetail({ asClient: true })

  const [addressData, setAddressData] = useState<AddressData | null>(null)
  const [showAddressErrors, setShowAddressErrors] = useState(false)
  const [areaRange, setAreaRange] = useState<ProductSegment | null>(null)

  const needsAddress = ADDRESS_REQUIRED_FIELDS.some((f) => missing.includes(f))
  const needsSurface = missing.includes('surface')

  // The range is what prices the products.
  const surfaceReady = !needsSurface || areaRange !== null

  // The re-entered address is only valid once it carries routable coordinates.
  const resolvedAddress = needsAddress ? toPropertyAddress(addressData) : null
  const addressReady = !needsAddress || resolvedAddress !== null

  const canSave = !patchProperty.isPending
    && addressReady
    && surfaceReady

  const submit = useCallback((): Promise<PropertyCompletionResult | null> => new Promise((resolve) => {
    if (!workspaceId) {
      spawnErrorToast(t('purchase_flow_revamp:drawer.complete_property_error'))
      resolve(null)
      return
    }

    if (needsAddress && !resolvedAddress) {
      // Address re-entered but not yet geocoded / incomplete — flag inputs.
      setShowAddressErrors(true)
      resolve(null)
      return
    }

    if (!canSave) {
      resolve(null)
      return
    }

    const pickedRange = needsSurface ? areaRange ?? undefined : undefined

    patchProperty.mutate(
      {
        workspaceId,
        propertyId,
        payload: {
          ...(resolvedAddress != null && { address: resolvedAddress }),
          ...(pickedRange && { areaRange: pickedRange }),
        },
      },
      {
        onSuccess: () => {
          spawnSuccessToast(t('purchase_flow_revamp:drawer.complete_property_success'), { timeout: 3000 })

          // Invalidate both the detail cache (used by usePropertyCompleteness)
          // and the list cache (used by PropertySearchList badges).
          queryClient.invalidateQueries({
            queryKey: [EntityKeys.PROPERTY, QueryType.GET_ONE, { propertyId, workspaceId }],
          })
          queryClient.invalidateQueries({
            queryKey: [EntityKeys.PROPERTY, QueryType.LIST, { workspaceId }],
          })

          onCompleted()
          resolve({ coordinates: resolvedAddress?.coordinates, address: resolvedAddress ?? undefined, areaRange: pickedRange })
        },
        onError: (error) => {
          spawnErrorToast(t('purchase_flow_revamp:drawer.complete_property_error'), { error: (error as any).request })
          resolve(null)
        },
      }
    )
  }), [
    workspaceId,
    propertyId,
    needsAddress,
    resolvedAddress,
    needsSurface,
    areaRange,
    canSave,
    patchProperty,
    queryClient,
    onCompleted,
    spawnErrorToast,
    spawnSuccessToast,
    t,
  ])

  useImperativeHandle(ref, () => ({ submit }), [submit])

  useEffect(() => {
    onStateChange?.({ canSave, isSaving: patchProperty.isPending })
  }, [canSave, onStateChange, patchProperty.isPending])

  return (
    <Box
      sx={{
        backgroundColor: BEIGE_400,
        borderRadius: '0.8rem',
        padding: '1.6rem',
        mt: '1.6rem',
      }}
    >

      <Stack gap={1.6}>

        <SystemMessage
          variant="info"
          message={t('purchase_flow_revamp:drawer.complete_property_body')}
        />

        {needsAddress &&
          <Stack gap={0.8}>

            <SystemMessage
              variant="warning"
              message={t('purchase_flow_revamp:drawer.complete_property_address_hint')}
            />

            <AddressSection
              showErrors={showAddressErrors}
              onAddressChange={setAddressData}
              onManualModeChange={() => undefined}
            />

          </Stack>
        }

        {needsSurface &&
          <Stack gap={1.2}>

            <SystemMessage
              variant="warning"
              message={t('purchase_flow_revamp:drawer.complete_property_area_range_hint')}
            />

            <AreaRangeFields
              areaRange={areaRange}
              onAreaRangeChange={setAreaRange}
            />

          </Stack>
        }

        {!hideSubmit &&
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <MUIButton
              size="md"
              disabled={!canSave}
              onClick={() => { void submit() }}
            >
              {patchProperty.isPending
                ? t('purchase_flow_revamp:drawer.completing_property')
                : t('purchase_flow_revamp:drawer.complete_property_save')}
            </MUIButton>
          </Box>
        }

      </Stack>

    </Box>
  )
})

PropertyCompletionForm.displayName = 'PropertyCompletionForm'
